import { useRef, useState, useEffect } from 'react';
import moment from 'moment';
import { CronExpressionParser } from 'cron-parser';
import { useSelector } from 'umi';
import { useMount, useUnmount } from 'ahooks';
import { fetchUserSearchTrendingStatus } from '@home/services/search';
import { CRON_EXPRESSIONS_MODEL_NAMESPACE } from '@home/models/cronExpressions';

import type { CronExpression } from 'cron-parser';
import type { ICRONExpressionsState } from '@home/models/cronExpressions';

/**
 * 获取指定cron表达式的状态（跟用户绑定）
 * @param key cron表达式的键
 */
const getCronExpressionState = async (key: string): Promise<string> => {
  try {
    const resData = await fetchUserSearchTrendingStatus({ action: 'get', keys: [key] });
    return resData?.[key] || '';
  } catch (err: any) {
    console.error(`getCronExpressionState错误: key为${key}, ${err.message}`);
    return '';
  }
};

/**
 * 更新指定cron表达式的状态（跟用户绑定）
 * @param key cron表达式的键
 * @param value cron表达式的状态
 */
const setCronExpressionState = async (key: string, value: string) => {
  await fetchUserSearchTrendingStatus({ action: 'set', key, value });
};

/**
 * 计算上一次季度更新时间
 * @param parser cron表达式解析器
 */
const getPrevTriggerDate = (parser?: CronExpression | null): INullableMoment => {
  if (!parser) return null;
  try {
    const prevTriggerDateM = moment(parser.prev().toDate());
    return prevTriggerDateM.isValid() ? prevTriggerDateM : null;
  } catch (err: any) {
    console.error(`计算上一次季度更新时间错误: ${err.message}`);
    return null;
  }
};

/**
 * 计算指定cron表达式配置的触发时间是否生效
 * @param key cron表达式的key
 * @param option 其他配置
 * @param option.durationDays 自然生效持续时间（单位为天），即cron表达式的时间触发后的durationDays天内 规则生效，超过durationDays天 就会自然失效
 */
export default function useCronExpressionCalc(key: string, option?: { durationDays?: number }) {
  const { durationDays = 30 } = option || {};

  /**
   * 获取系统cron表达式映射表
   */
  const { loading, data } = useSelector<any, ICRONExpressionsState>(
    (rootState) => rootState?.[CRON_EXPRESSIONS_MODEL_NAMESPACE] || {},
  );

  /**
   * 每日检测定时任务的定时器实例
   */
  const intervalInstance = useRef<NodeJS.Timeout | null>();

  /**
   * 是否展示
   */
  const [visible, setVisible] = useState(false);

  /**
   * 检查是否应该显示提示
   */
  const calcVisible = async () => {
    try {
      /**
       * cron表达式
       */
      const cronExpression = data?.[key]?.crontab || '';
      // 创建cron表达式解析parser
      const parser = CronExpressionParser.parse(cronExpression, { tz: 'Asia/Shanghai' });

      // 计算上一个季度更新时间 (当前季度的开始)
      const prevTriggerDate = getPrevTriggerDate(parser);
      if (!prevTriggerDate) return true;

      // 1. 检查用户是否已关闭提示
      const savedData = await getCronExpressionState(key);
      if (savedData) {
        const { closed_date: closedDate, trigger_date: triggerDate } = JSON.parse(savedData);

        // 如果用户已关闭，检查关闭时间是否在触发后的一个月内
        if (closedDate) {
          // 用户已手动关闭，则在下一次触发之前 继续不展示
          if (!prevTriggerDate.isAfter(moment(triggerDate))) {
            return false;
          }

          // 用户已手动关闭，则在下一次触发时 恢复展示
          const savedDataNew = { closed_date: '', trigger_date: prevTriggerDate.format('YYYY-MM-DD') };
          setCronExpressionState(key, JSON.stringify(savedDataNew));
          return true;
        }
      }

      // 计算距离上次更新的天数
      const diffTime = moment().utcOffset(8).valueOf() - prevTriggerDate.valueOf();
      const diffDays = diffTime / (24 * 60 * 60 * 1000);

      // 如果在上次更新后的30天内
      if (diffDays <= durationDays) {
        // 保存触发时间到localStorage
        const savedDataNew = JSON.stringify({
          trigger_date: prevTriggerDate.format('YYYY-MM-DD'),
          closed_date: '',
        });
        setCronExpressionState(key, savedDataNew);

        return true;
      }

      return false;
    } catch (err: any) {
      console.error(`calcVisible异常: ${err.message}`);
      return true;
    }
  };

  /**
   * 处理点击关闭
   */
  const handleClose = async () => {
    if (!visible) return;
    setVisible(false);

    try {
      // 获取当前触发时间
      const savedData = await getCronExpressionState(key);
      if (savedData) {
        const data = JSON.parse(savedData);
        // 更新关闭时间
        data.closed_date = moment().utcOffset(8).format('YYYY-MM-DD');
        setCronExpressionState(key, JSON.stringify(data));
      }
    } catch (err: any) {
      console.error(`设置Cron状态异常: ${err.message}`);
    }
  };

  /**
   * 初始化
   */
  const initialize = async () => {
    const visibleNew = await calcVisible();
    setVisible(visibleNew);
  };

  useEffect(() => {
    if (loading) return;
    initialize();
  }, [loading]);

  /**
   * 页面初始化，开启定时任务，每天检查一次提示状态
   */
  useMount(() => {
    intervalInstance.current = setInterval(() => initialize, 24 * 60 * 60 * 1000);
  });

  /**
   * 页面卸载时，清理定时任务，销毁定时器实例
   */
  useUnmount(() => {
    if (intervalInstance.current) {
      clearInterval(intervalInstance.current);
      intervalInstance.current = null;
    }
  });

  return { visible, onClose: handleClose };
}
